<template>
  <div class="management-analytics">
    <div class="management-analytics__header">
      <div class="management-analytics__title">
        Управленческий потенциал и ЭИ ресурс компании
      </div>

      <div class="management-analytics__filters">
        <a-select
            v-model:value="selectedGroup"
            :options="groupOptions"
            class="management-analytics__select"
        />

        <div>
          <a-range-picker
              v-model:value="selectedDate"
              :placeholder="['Дата начала', 'Дата конца']"
              format="DD.MM.YYYY"
              value-format="YYYY-MM-DD"
              class="management-analytics__date"
          />
        </div>
      </div>
    </div>

    <div class="management-analytics-grid">
      <SmallCardWrapper
          name="Управленческий потенциал"
          :helpText="'Управленческий потенциал'"
          :heightType="'medium'"
      >
        <div class="analytics-card">
          <div class="analytics-card__rows">
            <div
                v-for="item in managementPotential.items"
                :key="item.key"
                class="analytics-card__row"
            >
              <div class="analytics-card__row-name">
                <span
                    class="analytics-card__mark"
                    :style="{ backgroundColor: item.color }"
                ></span>

                <span>{{ item.label }}</span>
              </div>

              <span
                  class="analytics-card__change"
                  :class="getChangeClass(item.change)"
              >
                {{ getChangeText(item.change) }}
              </span>

              <span class="analytics-card__value">
                {{ item.value }} чел.
              </span>
            </div>
          </div>
          <div class="segment-bar">
            <div
                class="segment-bar__line"
                :class="{ 'segment-bar__line--empty': !hasSegmentValues(managementPotential.segments) }"
            >
              <template v-if="hasSegmentValues(managementPotential.segments)">
                <div
                    v-for="segment in getBarSegments(managementPotential.segments)"
                    :key="segment.key"
                    class="segment-bar__part"
                    :style="{
                      width: segment.percent + '%',
                      backgroundColor: segment.color
                    }"
                ></div>
              </template>
            </div>

            <div
                v-if="hasSegmentValues(managementPotential.segments)"
                class="segment-bar__values"
            >
              <span
                  v-for="segment in getBarSegments(managementPotential.segments)"
                  :key="segment.key"
                  class="segment-bar__value"
                  :style="{ width: segment.percent + '%' }"
              >
                {{ segment.value }}
              </span>
            </div>
          </div>
        </div>
      </SmallCardWrapper>

      <SmallCardWrapper
          name="Зеленые и красные флаги"
          :helpText="'Зеленые и красные флаги'"
          :heightType="'medium'"
      >
        <div class="analytics-card analytics-card--flags">
          <div class="flag-groups">
            <div
                v-for="group in flagsDetailed.groups"
                :key="group.key"
                class="flag-group"
            >
              <div class="flag-group__header">
                <div class="flag-group__title">
                  <span
                      class="analytics-card__mark"
                      :style="{ backgroundColor: group.color }"
                  ></span>

                  <span>{{ group.title }}</span>
                </div>

                <span class="analytics-card__value analytics-card__value--link">
                  {{ group.total }} чел.
                </span>
              </div>

              <div
                  v-for="item in group.items"
                  :key="item.label"
                  class="flag-group__row"
              >
                <span>{{ item.label }}</span>

                <span
                    class="analytics-card__change"
                    :class="getChangeClass(item.change, group.positiveIsGood)"
                >
                  {{ getChangeText(item.change) }}
                </span>

                <span class="analytics-card__value analytics-card__value--link">
                  {{ item.value }} чел.
                </span>
              </div>
            </div>
          </div>

          <div class="segment-bar">
            <div
                class="segment-bar__line"
                :class="{ 'segment-bar__line--empty': !hasSegmentValues(flagsDetailed.segments) }"
            >
              <template v-if="hasSegmentValues(flagsDetailed.segments)">
                <div
                    v-for="segment in getBarSegments(flagsDetailed.segments)"
                    :key="segment.key"
                    class="segment-bar__part"
                    :style="{
                      width: segment.percent + '%',
                      backgroundColor: segment.color
                    }"
                ></div>
              </template>
            </div>

            <div
                v-if="hasSegmentValues(flagsDetailed.segments)"
                class="segment-bar__values"
            >
              <span
                  v-for="segment in getBarSegments(flagsDetailed.segments)"
                  :key="segment.key"
                  class="segment-bar__value"
                  :style="{ width: segment.percent + '%' }"
              >
                {{ segment.value }}
              </span>
            </div>
          </div>
        </div>
      </SmallCardWrapper>

      <SmallCardWrapper
          name="ЭИ ресурс компании"
          :helpText="'ЭИ ресурс компании'"
      >
        <div class="analytics-card">
          <div>
            <div class="resource-card__score">
              {{ resource.score }}
            </div>

            <div class="resource-card__change">
              <span
                  :class="{
                    'positive-change': resource.change > 0,
                    'negative-change': resource.change < 0,
                    'neutral-change': resource.change === 0
                  }"
              >
                {{ resource.level }}: {{ getSignedPercent(resource.change) }}
              </span>

              <span>{{ resource.changeLabel }}</span>
            </div>

            <div class="analytics-card__rows">
              <div
                  v-for="item in resource.items"
                  :key="item.key"
                  class="analytics-card__row"
              >
                <div class="analytics-card__row-name">
                  <span
                      class="analytics-card__mark"
                      :style="{ backgroundColor: item.color }"
                  ></span>

                  <span>{{ item.label }}</span>
                </div>

                <span
                    class="analytics-card__change"
                    :class="getChangeClass(item.change)"
                >
                  {{ getChangeText(item.change) }}
                </span>

                <span class="analytics-card__value">
                  {{ item.value }} чел.
                </span>
              </div>
            </div>
          </div>

          <div class="segment-bar">
            <div
                class="segment-bar__line"
                :class="{ 'segment-bar__line--empty': !hasSegmentValues(resource.segments) }"
            >
              <template v-if="hasSegmentValues(resource.segments)">
                <div
                    v-for="segment in getBarSegments(resource.segments)"
                    :key="segment.key"
                    class="segment-bar__part"
                    :style="{
                      width: segment.percent + '%',
                      backgroundColor: segment.color
                    }"
                ></div>
              </template>
            </div>

            <div
                v-if="hasSegmentValues(resource.segments)"
                class="segment-bar__values"
            >
              <span
                  v-for="segment in getBarSegments(resource.segments)"
                  :key="segment.key"
                  class="segment-bar__value"
                  :style="{ width: segment.percent + '%' }"
              >
                {{ segment.value }}
              </span>
            </div>
          </div>
        </div>
      </SmallCardWrapper>

      <SmallCardWrapper
          name="Соотношение доменов"
          :helpText="'Соотношение доменов'"
      >
        <div class="analytics-card analytics-card--domains">
          <DomainsDonutChart :items="domains" />

          <div class="domain-card__legend">
            <div
                v-for="item in normalizedDomains"
                :key="item.key"
                class="domain-card__legend-row"
            >
              <span
                  class="analytics-card__mark"
                  :style="{ backgroundColor: item.color }"
              ></span>

              <span>
                {{ item.label }} —
                <span class="analytics-card__value analytics-card__value--link">
                  {{ item.value }} чел.
                </span>
              </span>
            </div>
          </div>
        </div>
      </SmallCardWrapper>

      <SmallCardWrapper
          name="Зеленые и красные флаги"
          :helpText="'Зеленые и красные флаги'"
      >
        <div class="analytics-card">
          <div class="analytics-card__rows">
            <div
                v-for="item in flagsSimple.items"
                :key="item.key"
                class="analytics-card__row"
            >
              <div class="analytics-card__row-name">
                <span
                    class="analytics-card__mark"
                    :style="{ backgroundColor: item.color }"
                ></span>

                <span>{{ item.label }}</span>
              </div>

              <span
                  class="analytics-card__change"
                  :class="getChangeClass(item.change)"
              >
                {{ getChangeText(item.change) }}
              </span>

              <span class="analytics-card__value">
                {{ item.value }} чел.
              </span>
            </div>
          </div>

          <div class="segment-bar">
            <div
                class="segment-bar__line"
                :class="{ 'segment-bar__line--empty': !hasSegmentValues(flagsSimple.segments) }"
            >
              <template v-if="hasSegmentValues(flagsSimple.segments)">
                <div
                    v-for="segment in getBarSegments(flagsSimple.segments)"
                    :key="segment.key"
                    class="segment-bar__part"
                    :style="{
                      width: segment.percent + '%',
                      backgroundColor: segment.color
                    }"
                ></div>
              </template>
            </div>

            <div
                v-if="hasSegmentValues(flagsSimple.segments)"
                class="segment-bar__values"
            >
              <span
                  v-for="segment in getBarSegments(flagsSimple.segments)"
                  :key="segment.key"
                  class="segment-bar__value"
                  :style="{ width: segment.percent + '%' }"
              >
                {{ segment.value }}
              </span>
            </div>
          </div>
        </div>
      </SmallCardWrapper>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import SmallCardWrapper from '@/components/Widgets/WidgetCard.vue'
import DomainsDonutChart from '@/components/Analytics/DomainsDonutChart.vue'
import {useStore} from "vuex";

export default {
  name: 'ManagementAnalyticsCards',

  components: {
    SmallCardWrapper,
    DomainsDonutChart,
  },

  setup() {
    const store = useStore();
    const groups = computed(() => store.state.group.groups);

    const selectedDate = ref();
    const selectedGroup = ref('all');

    const groupOptions = computed(() => {
      return [
        {
          label: 'Все сотрудники',
          value: 'all',
        },
        ...groups.value.map((group) => ({
          label: group.name,
          value: group.id,
        })),
      ]
    });

    const managementPotential = {
      items: [
        {
          key: 'highest',
          label: 'Высший',
          change: 10,
          value: 13,
          color: '#21AC0C',
        },
        {
          key: 'high',
          label: 'Высокий',
          change: -8,
          value: 18,
          color: '#8BD05B',
        },
        {
          key: 'moderate',
          label: 'Умеренный',
          change: 10,
          value: 350,
          color: '#DDE00F',
        },
        {
          key: 'risk',
          label: 'С рисками',
          change: -150,
          value: 12,
          color: '#FF9F40',
        },
        {
          key: 'low',
          label: 'Низкий',
          change: 10,
          value: 5,
          color: '#F55565',
        },
      ],
      segments: [
        {
          key: 'low',
          value: 5,
          color: '#F55565',
        },
        {
          key: 'risk',
          value: 12,
          color: '#FF9F40',
        },
        {
          key: 'moderate',
          value: 350,
          color: '#DDE00F',
        },
        {
          key: 'high',
          value: 18,
          color: '#8BD05B',
        },
        {
          key: 'highest',
          value: 13,
          color: '#21AC0C',
        },
      ],
    }

    const flagsDetailed = {
      groups: [
        {
          key: 'balanced',
          title: 'Сотрудники с балансом ЭИ',
          total: 20,
          color: '#21AC0C',
          positiveIsGood: true,
          items: [
            {
              label: 'В высоких границах, < 120',
              change: -2,
              value: 13,
            },
            {
              label: 'В средних границах, 100–120',
              change: 1,
              value: 7,
            },
          ],
        },
        {
          key: 'risks',
          title: 'Сотрудники с рисками',
          total: 81,
          color: '#F55565',
          positiveIsGood: false,
          items: [
            {
              label: 'Выгорание',
              change: 10,
              value: 21,
            },
            {
              label: 'Манипулятивность',
              change: 0,
              value: 13,
            },
            {
              label: 'Не способен влиять на эмоции других',
              change: 100,
              value: 35,
            },
            {
              label: 'Риск импульсивности',
              change: 10,
              value: 12,
            },
          ],
        },
        {
          key: 'moderate',
          title: 'Умеренный профиль',
          total: 100,
          color: '#D5DBE2',
          positiveIsGood: true,
          items: [],
        },
      ],
      segments: [
        {
          key: 'risks',
          value: 81,
          color: '#F55565',
        },
        {
          key: 'moderate',
          value: 100,
          color: '#D5DBE2',
        },
        {
          key: 'balanced',
          value: 20,
          color: '#21AC0C',
        },
      ],
    }

    const resource = {
      score: 105,
      level: 'Высокий',
      change: 10,
      changeLabel: 'за неделю',
      items: [
        {
          key: 'highest',
          label: 'Высший',
          change: 7,
          value: 13,
          color: '#21AC0C',
        },
        {
          key: 'high',
          label: 'Высокий',
          change: 10,
          value: 46,
          color: '#8BD05B',
        },
        {
          key: 'medium',
          label: 'Средний',
          change: 2,
          value: 12,
          color: '#FF9F40',
        },
        {
          key: 'low',
          label: 'Низкий',
          change: -7,
          value: 5,
          color: '#F55565',
        },
      ],
      segments: [
        {
          key: 'low',
          value: 5,
          color: '#F55565',
        },
        {
          key: 'medium',
          value: 12,
          color: '#FF9F40',
        },
        {
          key: 'high',
          value: 46,
          color: '#8BD05B',
        },
        {
          key: 'highest',
          value: 13,
          color: '#21AC0C',
        },
      ],
    }

    const domains = [
      {
        key: 'drivers',
        label: 'Драйверы',
        value: 64,
        color: '#9B8CFF',
      },
      {
        key: 'strategists',
        label: 'Стратеги',
        value: 25,
        color: '#67DCE0',
      },
      {
        key: 'balanced',
        label: 'Сбалансированные',
        value: 6,
        color: '#D5DBE2',
      },
    ]

    const flagsSimple = {
      items: [
        {
          key: 'green',
          label: 'ЭИ > 120',
          change: 7,
          value: 13,
          color: '#21AC0C',
        },
        {
          key: 'neutral',
          label: 'ЭИ 80 — 120',
          change: 10,
          value: 46,
          color: '#D5DBE2',
        },
        {
          key: 'red',
          label: 'ЭИ < 80',
          change: 2,
          value: 12,
          color: '#F55565',
        },
      ],
      segments: [
        {
          key: 'red',
          value: 12,
          color: '#F55565',
        },
        {
          key: 'neutral',
          value: 46,
          color: '#D5DBE2',
        },
        {
          key: 'green',
          value: 13,
          color: '#21AC0C',
        },
      ],
    }

    const toNumber = (value) => {
      const number = Number(value)

      return Number.isFinite(number) ? number : 0
    }

    const normalizedDomains = computed(() => {
      return domains.map((item) => ({
        ...item,
        value: toNumber(item.value),
      }))
    })

    const getSegmentsTotal = (segments) => {
      return segments.reduce((sum, segment) => {
        return sum + toNumber(segment.value)
      }, 0)
    }

    const hasSegmentValues = (segments) => {
      return getSegmentsTotal(segments) > 0
    }

    const getBarSegments = (segments) => {
      const total = getSegmentsTotal(segments)

      if (!total) {
        return []
      }

      return segments
          .map((segment) => ({
            ...segment,
            value: toNumber(segment.value),
          }))
          .filter(segment => segment.value > 0)
          .map((segment) => ({
            ...segment,
            percent: Number(((segment.value / total) * 100).toFixed(2)),
          }))
    }

    const getSignedPercent = (value) => {
      const number = toNumber(value)

      if (number > 0) {
        return `+${number}%`
      }

      return `${number}%`
    }

    const getChangeText = (value) => {
      const number = Number(value)

      if (!Number.isFinite(number) || number === 0) {
        return '–'
      }

      if (number > 0) {
        return `↑ ${number}`
      }

      return `↓ ${Math.abs(number)}`
    }

    const getChangeClass = (value, positiveIsGood = true) => {
      const number = Number(value)

      if (!Number.isFinite(number) || number === 0) {
        return 'neutral-change'
      }

      if (positiveIsGood) {
        return number > 0 ? 'positive-change' : 'negative-change'
      }

      return number > 0 ? 'negative-change' : 'positive-change'
    }

    return {
      selectedGroup,
      selectedDate,
      groupOptions,
      managementPotential,
      flagsDetailed,
      resource,
      domains,
      normalizedDomains,
      flagsSimple,
      hasSegmentValues,
      getBarSegments,
      getSignedPercent,
      getChangeText,
      getChangeClass,
    }
  },
}
</script>

<style scoped lang="scss">
.management-analytics {
  width: 100%;
  text-align: start;
}

.management-analytics__header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.management-analytics__title {
  margin: 0 0 12px;
  font-weight: 500;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.management-analytics__filters {
  display: flex;
  align-items: center;
  gap: 12px;
}

.management-analytics__select {
  width: 350px;
}

.management-analytics__date {
  width: 200px;
}

.management-analytics-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.management-analytics-grid :deep(.ant-card-body) {
  padding: 0;
}

.management-analytics-grid :deep(.small-card-wrapper__body) {
  align-items: stretch;
}

.analytics-card {
  width: 100%;
  min-height: 170px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.analytics-card__rows {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.analytics-card__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px 64px;
  align-items: center;
  column-gap: 12px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.analytics-card__row-name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.analytics-card__row-name span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.analytics-card__mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.analytics-card__change {
  text-align: right;
  white-space: nowrap;
}

.analytics-card__value {
  color: rgba(0, 0, 0, 0.88);
  text-align: right;
  white-space: nowrap;
}

.analytics-card__value--link {
  text-decoration: underline;
  cursor: pointer;
}

.positive-change {
  color: var(--main-green);
}

.negative-change {
  color: #F55565;
}

.neutral-change {
  color: rgba(0, 0, 0, 0.45);
}

.segment-bar {
  width: 100%;
  margin-top: 12px;
}

.segment-bar__line {
  display: flex;
  width: 100%;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #F2F4F7;
  gap: 2px;
}

.segment-bar__line--empty {
  background: #D5DBE2;
}

.segment-bar__part {
  min-width: 0;
  height: 8px;
}

.segment-bar__values {
  display: flex;
  width: 100%;
  margin-top: 5px;
  font-size: 11px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.45);
}

.segment-bar__value {
  min-width: 0;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flag-groups {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.flag-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.flag-group__header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 64px;
  align-items: center;
  column-gap: 12px;
  font-size: 14px;
  line-height: 120%;
}

.flag-group__title {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.flag-group__title span:last-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flag-group__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 44px 64px;
  align-items: center;
  column-gap: 12px;
  padding-left: 16px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.flag-group__row span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.resource-card__score {
  margin-bottom: 4px;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.resource-card__change {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.resource-card__change span:first-child {
  font-weight: 600;
}

.analytics-card--domains {
  min-height: 170px;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 28px;
}

.domain-card__legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.domain-card__legend-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
  white-space: nowrap;
}

@media (max-width: $tablet-breakpoint) {
  .management-analytics-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: $mobile-breakpoint) {
  .management-analytics__filters {
    flex-direction: column;
    align-items: stretch;
  }

  .management-analytics__select,
  .management-analytics__date {
    width: 100%;
  }

  .analytics-card--domains {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>